import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { NotesQuery } from '@modules/notes/state/notes-query';
import { NotesService } from '@modules/notes/services/notes.service';
import { RxNote } from '@modules/notes/models/rx-note';
import { RoleTypeEnum } from '@shared/models/role-type';
import { IntuitiveNote } from './models/intuitive-note.model';

const noteId = (note: RxNote): string => (note.id == null ? '' : String(note.id));

const rxNoteToIntuitive = (note: RxNote, currentUserRole: RoleTypeEnum): IntuitiveNote => ({
	id: noteId(note),
	createdByName: note.createdByName ?? null,
	dateCreated: note.dateCreated ? new Date(note.dateCreated) : new Date(),
	content: note.content ?? '',
	canModify: canModifyNote(note, currentUserRole)
});

const canModifyNote = (note: RxNote, currentUserRole: RoleTypeEnum): boolean => note.role === currentUserRole;

@Injectable()
export class IntuitiveNotesListFacade {
	notes$: Observable<IntuitiveNote[]> = this.notesQuery.notesArray$.pipe(
		map(notes => (notes ?? []).map(note => rxNoteToIntuitive(note, this.shellQuery.userRole)))
	);

	constructor(
		private readonly notesService: NotesService,
		private readonly notesQuery: NotesQuery,
		private readonly shellQuery: ShellQuery
	) {}

	submitUnsavedNote(content: string | null | undefined): void {
		const trimmed = content?.trim();

		if (!trimmed) {
			return;
		}

		this.addNote(trimmed);
	}

	addNote(content: string, createdByName: string | null = null): void {
		const newNote = this.notesService.createNote({
			content,
			createdByName: createdByName ?? this.shellQuery.user?.Name ?? undefined,
			createdById: this.shellQuery.contactId ?? undefined,
			dateCreated: new Date().toISOString(),
			role: this.shellQuery.userRole ?? undefined
		});

		const updated = [newNote, ...(this.notesQuery.notesArray ?? [])];

		this.notesService.updateNotes(updated);
	}

	editNote(id: string, content: string): void {
		const notes = this.notesQuery.notesArray ?? [];

		if (!this.isModifiableById(notes, id)) {
			return;
		}

		const updated = notes.map(note => (noteId(note) === id ? { ...note, content } : note));

		this.notesService.updateNotes(updated);
	}

	removeNote(id: string): void {
		const notes = this.notesQuery.notesArray ?? [];

		if (!this.isModifiableById(notes, id)) {
			return;
		}

		const updated = notes.filter(note => noteId(note) !== id);

		this.notesService.updateNotes(updated);
	}

	clearNotes(): void {
		this.notesService.updateNotes([]);
	}

	private isModifiableById(notes: RxNote[], id: string): boolean {
		const noteToModify = notes.find(note => noteId(note) === id);

		return !!noteToModify && canModifyNote(noteToModify, this.shellQuery.userRole);
	}
}
